import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccessibilityState {
  /** Disables CSS animations / transitions (also respects OS prefers-reduced-motion) */
  reducedMotion: boolean;
  /** Replaces color-only distinctions with shapes + patterns + labels */
  colorBlindMode: boolean;
  toggleReducedMotion: () => void;
  toggleColorBlindMode: () => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AccessibilityContext = createContext<AccessibilityState>({
  reducedMotion: false,
  colorBlindMode: false,
  toggleReducedMotion: () => {},
  toggleColorBlindMode: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

const LS_KEY_MOTION = 'a11y-reduced-motion';
const LS_KEY_CB     = 'a11y-color-blind';

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [reducedMotion, setReducedMotion] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_KEY_MOTION) === 'true' || prefersReducedMotion; }
    catch { return prefersReducedMotion; }
  });

  const [colorBlindMode, setColorBlindMode] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_KEY_CB) === 'true'; }
    catch { return false; }
  });

  const toggleReducedMotion = useCallback(() => {
    setReducedMotion(v => {
      const next = !v;
      try { localStorage.setItem(LS_KEY_MOTION, String(next)); } catch {}
      return next;
    });
  }, []);

  const toggleColorBlindMode = useCallback(() => {
    setColorBlindMode(v => {
      const next = !v;
      try { localStorage.setItem(LS_KEY_CB, String(next)); } catch {}
      return next;
    });
  }, []);

  // Apply / remove utility classes on <html> so CSS can react
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('reduced-motion', reducedMotion);
    root.classList.toggle('color-blind-mode', colorBlindMode);
  }, [reducedMotion, colorBlindMode]);

  return (
    <AccessibilityContext.Provider
      value={{ reducedMotion, colorBlindMode, toggleReducedMotion, toggleColorBlindMode }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAccessibility(): AccessibilityState {
  return useContext(AccessibilityContext);
}
